import React, { useEffect, useState, useRef } from 'react';
import { Form, Input, Select, Button, Space, Radio, Card, Row, Col, Divider, Typography, Segmented, Popover, List, Tooltip, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, CodeOutlined, UnorderedListOutlined, InsertRowBelowOutlined, ClockCircleOutlined } from '@ant-design/icons';
import globalVariableService from '../../../services/GlobalVariableService';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// Custom Input component that shows a list of columns on click/focus and inserts selected column at cursor
const FormulaInput = ({ value = '', onChange, options = [], placeholder, rows = 4 }) => {
  const [open, setOpen] = useState(false);
  const [filterToken, setFilterToken] = useState('');
  const inputRef = useRef(null);

  // Helper to find the word at cursor
  const getWordAtCursor = (text, position) => {
    const left = text.slice(0, position);
    // Match word characters (letters, numbers, underscore) at the end of the string
    const match = left.match(/[a-zA-Z0-9_]+$/);
    return match ? { word: match[0], start: match.index } : { word: '', start: position };
  };

  const updateFilter = (target) => {
    const pos = target.selectionStart;
    const val = target.value;
    const { word } = getWordAtCursor(val, pos);
    setFilterToken(word);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    updateFilter(e.target);
    if (!open) setOpen(true);
  };

  const handleClick = (e) => {
    updateFilter(e.target);
    if (open) {
      e.stopPropagation();
    }
  };

  const handleKeyUp = (e) => {
    updateFilter(e.target);
  };

  const handleInsert = (text) => {
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;

    // Auto-quote column names with double quotes for SQL
    const textToInsert = `"${text}"`;

    if (input) {
      const currentPos = input.selectionStart || 0;
      const { word, start } = getWordAtCursor(value, currentPos);

      const val = value || '';
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      onChange(newValue);

      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken('');
      }, 0);
    } else {
      onChange((value || '') + textToInsert);
    }
    setOpen(false);
  };

  const filteredOptions = options.filter(opt =>
    !filterToken || opt.value.toLowerCase().includes(filterToken.toLowerCase())
  );

  // Get global variables from service
  const globalVariables = globalVariableService.getAllVariables();
  const variableEntries = Object.entries(globalVariables);
  const filteredVariables = variableEntries.filter(([name]) =>
    !filterToken || name.toLowerCase().includes(filterToken.toLowerCase())
  );

  const handleInsertVariable = (varName) => {
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;
    const textToInsert = `{{${varName}}}`;
    if (input) {
      const currentPos = input.selectionStart || 0;
      const { start } = getWordAtCursor(value, currentPos);
      const val = value || '';
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      onChange(newValue);
      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken('');
      }, 0);
    } else {
      onChange((value || '') + textToInsert);
    }
    setOpen(false);
  };

  const content = (
    <div style={{ width: 300, maxHeight: 350, overflowY: 'auto' }}>
      {/* Columns Section */}
      <div style={{ padding: '4px 8px', background: '#f5f5f5', borderBottom: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong>Columns</Text> {filterToken && <Text type="secondary" style={{ fontSize: 11 }}>(filtering by "{filterToken}")</Text>}
      </div>
      <List
        dataSource={filteredOptions}
        style={{ maxHeight: 120, overflowY: 'auto' }}
        renderItem={item => (
          <List.Item
            onClick={() => handleInsert(item.value)}
            style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e6f7ff'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Text code>{item.value}</Text>
          </List.Item>
        )}
        locale={{ emptyText: <Text type="secondary" style={{ fontSize: 11 }}>No matching columns</Text> }}
      />

      {/* Global Variables Section */}
      <div style={{ padding: '4px 8px', background: '#f6ffed', borderBottom: '1px solid #b7eb8f', borderTop: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong style={{ color: '#52c41a' }}><ClockCircleOutlined /> Variables</Text>
      </div>
      {filteredVariables.length > 0 ? (
        <List
          dataSource={filteredVariables}
          style={{ maxHeight: 150, overflowY: 'auto' }}
          renderItem={([name, varData]) => (
            <List.Item
              onClick={() => handleInsertVariable(name)}
              style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f6ffed'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Space>
                <Tag color="green">{`{{${name}}}`}</Tag>
                <Text type="secondary" style={{ fontSize: 10 }}>{typeof varData === 'object' ? varData.value : varData}</Text>
              </Space>
            </List.Item>
          )}
        />
      ) : (
        <div style={{ padding: '8px 12px', color: '#999', fontSize: 11 }}>No variables defined</div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <div style={{ position: 'relative' }}>
        <Input.TextArea
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          placeholder={placeholder}
          rows={rows}
          style={{ paddingRight: 30, fontFamily: 'monospace', fontSize: '12px' }}
        />
        <Tooltip title="Click to insert column or variable">
          <InsertRowBelowOutlined
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#bfbfbf',
              cursor: 'pointer',
              zIndex: 1
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          />
        </Tooltip>
      </div>
    </Popover>
  );
};

const getReverseOperator = (operator) => {
  const map = {
    '>': '<=',
    '<': '>=',
    '>=': '<',
    '<=': '>',
    '==': '!=',
    '!=': '==',
    'contains': 'not contains',
    'startsWith': 'not startsWith',
    'in': 'not in',
    'not_in': 'in'
  };
  return map[operator] || `!${operator}`;
};

const FilterNodeProperties = ({ config, nodeType, inputSchema, updateConfig, updateNodeName }) => {
  const [form] = Form.useForm();
  const [currentLogic, setCurrentLogic] = useState(config.logic || 'AND');
  const [filterMode, setFilterMode] = useState(config.filterMode || 'conditions');

  // Sync form with config when it changes
  useEffect(() => {
    if (nodeType === 'if_else' || nodeType === 'filter_rows') {
      // Normalize config for if_else and filter_rows to support multiple conditions
      // If legacy format (field, operator, value at root), convert to conditions array
      if (!config.conditions && config.field) {
        const initialLogic = config.logic || 'AND';
        setCurrentLogic(initialLogic);
        const mode = config.filterMode || 'conditions';
        setFilterMode(mode);
        form.setFieldsValue({
          ...config,
          logic: initialLogic,
          filterMode: mode,
          conditions: [{ field: config.field, operator: config.operator, value: config.value }]
        });
      } else {
        const initialLogic = config.logic || 'AND';
        setCurrentLogic(initialLogic);
        const mode = config.filterMode || 'conditions';
        setFilterMode(mode);
        form.setFieldsValue({
          ...config,
          logic: initialLogic,
          filterMode: mode,
          conditions: config.conditions || []
        });
      }
    } else {
      form.setFieldsValue(config);
    }
  }, [config, form, nodeType]);

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues.logic) {
      setCurrentLogic(changedValues.logic);
    }

    if (updateConfig) {
      // Handle operator change in conditions to reset value type if needed
      if (changedValues.conditions) {
        const updatedConditions = [...(allValues.conditions || [])];
        let needsUpdate = false;

        changedValues.conditions.forEach((changedCond, index) => {
          if (changedCond && changedCond.operator !== undefined) {
            const currentCond = updatedConditions[index];
            const prevCond = config.conditions?.[index];

            const isMultiValue = ['in', 'not_in'].includes(changedCond.operator);
            const wasMultiValue = prevCond ? ['in', 'not_in'].includes(prevCond.operator) : false;

            // Only reset value when switching between multi-value and single-value operators
            if (isMultiValue !== wasMultiValue && currentCond) {
              if (isMultiValue) {
                // Convert string to array
                const currentValue = currentCond.value;
                if (typeof currentValue === 'string' && currentValue) {
                  updatedConditions[index] = {
                    ...currentCond,
                    value: currentValue.split(',').map(s => s.trim()).filter(s => s)
                  };
                } else if (!Array.isArray(currentValue)) {
                  updatedConditions[index] = { ...currentCond, value: [] };
                }
              } else {
                // Convert array to string
                const currentValue = currentCond.value;
                if (Array.isArray(currentValue)) {
                  updatedConditions[index] = {
                    ...currentCond,
                    value: currentValue.join(', ')
                  };
                }
              }
              needsUpdate = true;
            }
          }
        });

        if (needsUpdate) {
          allValues.conditions = updatedConditions;
          form.setFieldsValue({ conditions: updatedConditions });
        }
      }

      const updatePayload = {
        config: allValues
      };

      // Auto-update node name for filter nodes
      // Only update name when we have valid complete conditions, don't reset to default while editing
      if (nodeType === 'filter_rows') {
        const { conditions, logic } = allValues;

        if (conditions && conditions.length > 0) {
          const parts = [];

          conditions.forEach(cond => {
            if (cond && cond.field && cond.operator && cond.value !== undefined) {
              let displayValue = cond.value;
              if (Array.isArray(cond.value)) {
                displayValue = `[${cond.value.join(', ')}]`;
              }
              parts.push(`${cond.field} ${cond.operator} ${displayValue}`);
            }
          });

          if (parts.length > 0) {
            const joiner = logic === 'OR' ? ' OR ' : ' AND ';
            const conditionStr = parts.length > 1 ? parts.map(p => `(${p})`).join(joiner) : parts[0];

            const newName = `Filter: ${conditionStr}`;
            updatePayload.customName = newName;
            updatePayload.label = newName;

            if (updateNodeName) updateNodeName(newName);
          }
          // Don't reset to default name while editing - just keep current name
        }
        // Don't reset to 'Filter Rows' - keep existing name while user is editing
      } else if (nodeType === 'if_else') {
        const { conditions, logic } = allValues;

        if (conditions && conditions.length > 0) {
          const parts = [];
          const reverseParts = [];

          conditions.forEach(cond => {
            if (cond && cond.field && cond.operator && cond.value !== undefined) {
              let displayValue = cond.value;
              if (Array.isArray(cond.value)) {
                displayValue = `[${cond.value.join(', ')}]`;
              }

              parts.push(`${cond.field} ${cond.operator} ${displayValue}`);

              const revOp = getReverseOperator(cond.operator);
              reverseParts.push(`${cond.field} ${revOp} ${displayValue}`);
            }
          });

          if (parts.length > 0) {
            const joiner = logic === 'OR' ? ' OR ' : ' AND ';
            const revJoiner = logic === 'OR' ? ' AND ' : ' OR '; // De Morgan's laws

            const conditionStr = parts.length > 1 ? parts.map(p => `(${p})`).join(joiner) : parts[0];
            const reverseConditionStr = reverseParts.length > 1 ? reverseParts.map(p => `(${p})`).join(revJoiner) : reverseParts[0];

            updatePayload.config = {
              ...allValues,
              edgeLabel: conditionStr,
              edgeLabelElse: reverseConditionStr
            };

            const newName = `If: ${conditionStr}`;
            updatePayload.customName = newName;
            updatePayload.label = newName;

            if (updateNodeName) updateNodeName(newName);
          }
          // Don't reset to default name while editing - just keep current name
        }
        // Don't reset to 'If-Else' - keep existing name while user is editing
      }

      updateConfig(updatePayload);
    }
  };

  if (nodeType === 'filter_columns') {
    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={config}
        onValuesChange={handleValuesChange}
      >
        <Form.Item label="Action" name="action" initialValue={config.action || 'include'}>
          <Select>
            <Option value="include">Include columns</Option>
            <Option value="exclude">Exclude columns</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Columns" name="columns" initialValue={config.columns || []}>
          <Select mode="tags" placeholder="Select or enter column names">
            {inputSchema && inputSchema.length > 0 ? (
              inputSchema.map(col => (
                <Option key={col.name} value={col.name}>{col.name}</Option>
              ))
            ) : null}
          </Select>
        </Form.Item>
      </Form>
    );
  }

  // filter_rows and if_else
  if (nodeType === 'if_else' || nodeType === 'filter_rows') {
    const handleModeChange = (mode) => {
      setFilterMode(mode);
      // Update hidden field to keep form in sync
      form.setFieldsValue({ filterMode: mode });
      if (updateConfig) {
        // Get all current form values including conditions to preserve them
        const currentValues = form.getFieldsValue(true);
        updateConfig({ config: { ...currentValues, filterMode: mode } });
      }
    };

    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={config}
        onValuesChange={handleValuesChange}
      >
        {/* Filter Mode Selector */}
        <Form.Item label="Filter Mode" style={{ marginBottom: 16 }}>
          <Segmented
            value={filterMode}
            onChange={handleModeChange}
            options={[
              { label: 'Conditions', value: 'conditions', icon: <UnorderedListOutlined /> },
              { label: 'SQL Formula', value: 'sql', icon: <CodeOutlined /> },
            ]}
            block
          />
        </Form.Item>

        <Form.Item name="filterMode" hidden initialValue="conditions">
          <Input />
        </Form.Item>

        <Form.Item name="logic" hidden initialValue="AND">
          <Input />
        </Form.Item>

        {/* SQL Formula Mode */}
        {filterMode === 'sql' && (
          <>
            <Form.Item
              label="SQL WHERE Clause"
              name="sqlFormula"
              initialValue={config.sqlFormula || ''}
              rules={[{ required: true, message: 'Please enter SQL formula' }]}
            >
              <FormulaInput
                placeholder={'Example:\n"Sales" > 1000 AND "Status" = \'Active\'\n"ACOS" < 25 OR "Impressions" > 10000'}
                options={inputSchema?.map(col => ({ value: col.name, label: col.name })) || []}
                rows={12}
              />
            </Form.Item>
            <div style={{ padding: '8px 12px', backgroundColor: '#f6ffed', borderRadius: '4px', fontSize: '11px', marginBottom: 16, border: '1px solid #b7eb8f' }}>
              <Text type="secondary">
                <strong>SQL Formula Tips:</strong><br />
                • Click the icon or start typing to insert column names<br />
                • Use single quotes for text values: <code>'value'</code><br />
                • Operators: <code>=, !=, &gt;, &lt;, &gt;=, &lt;=, LIKE, IN, NOT IN</code><br />
                • Logic: <code>AND, OR, NOT</code><br />
                • Example: <code>"Price" &gt; 100 AND "Category" IN ('A', 'B')</code>
              </Text>
            </div>
          </>
        )}

        {/* Conditions Mode (existing UI) */}
        {filterMode === 'conditions' && (
          <Form.List name="conditions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <React.Fragment key={key}>
                    {index > 0 && (
                      <Divider style={{ margin: '12px 0', fontSize: '12px', color: '#8c8c8c' }}>
                        <Select
                          value={currentLogic}
                          onChange={(val) => {
                            form.setFieldsValue({ logic: val });
                            // Manually trigger change handling since programmatic updates don't trigger onValuesChange
                            handleValuesChange({ logic: val }, { ...form.getFieldsValue(), logic: val });
                          }}

                          style={{ width: 100, textAlign: 'center' }}
                          bordered={false}
                          dropdownMatchSelectWidth={false}
                        >
                          <Option value="AND">AND</Option>
                          <Option value="OR">OR</Option>
                        </Select>
                      </Divider>
                    )}
                    <Card style={{ backgroundColor: '#fafafa', borderColor: '#d9d9d9' }} bodyStyle={{ padding: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: 4 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'field']}
                            rules={[{ required: true, message: 'Missing field' }]}
                            style={{ marginBottom: 8 }}
                          >
                            {inputSchema && inputSchema.length > 0 ? (
                              <Select placeholder="Select column" showSearch >
                                {inputSchema.map(col => (
                                  <Option key={col.name} value={col.name}>{col.name}</Option>
                                ))}
                              </Select>
                            ) : (
                              <Input placeholder="Column name" />
                            )}
                          </Form.Item>
                        </div>

                        <div style={{ flex: 1 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'operator']}
                            initialValue=">"
                            style={{ marginBottom: 0, width: 120 }}
                          >
                            <Select
                              placeholder="Operator"
                            >
                              <Option value=">">{`>`}</Option>
                              <Option value="<">{`<`}</Option>
                              <Option value=">=">{`>=`}</Option>
                              <Option value="<=">{`<=`}</Option>
                              <Option value="==">{`==`}</Option>
                              <Option value="!=">{`!=`}</Option>
                              <Option value="contains">Contains</Option>
                              <Option value="startsWith">Starts with</Option>
                              <Option value="in">IN</Option>
                              <Option value="not_in">NOT IN</Option>
                            </Select>
                          </Form.Item>
                        </div>

                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: 2 }}>
                          <Form.Item noStyle shouldUpdate={(prev, curr) => {
                            const prevOp = prev?.conditions?.[name]?.operator;
                            const currOp = curr?.conditions?.[name]?.operator;
                            return prevOp !== currOp;
                          }}>
                            {({ getFieldValue }) => {
                              const operator = getFieldValue(['conditions', name, 'operator']);
                              const isMultiValue = ['in', 'not_in'].includes(operator);

                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  rules={[{ required: true, message: 'Missing value' }]}
                                  style={{ marginBottom: 0, flex: 1 }}
                                >
                                  {isMultiValue ? (
                                    <Select
                                      mode="tags"
                                      placeholder="Enter values and press Enter"
                                      tokenSeparators={[',']}
                                      style={{ width: '100%' }}
                                      dropdownStyle={{ display: 'none' }}
                                      notFoundContent={null}
                                    />
                                  ) : (
                                    <Input placeholder="Value" />
                                  )}
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </div>
                      </div>
                    </Card>
                  </React.Fragment>
                ))}
                <Form.Item style={{ marginTop: 16 }}>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Condition
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        )}
      </Form >
    );
  }

  return null;
};

export default FilterNodeProperties;